import React, { useState, useEffect } from 'react';
import {
	SdfFocusPane,
	SdfWizardStepsVertical,
	SdfWizardStepsVerticalItem,
	SdfButton,
	SdfIcon,
	SdfWizardLayout,
	SdfAlert,
	SdfBusyIndicator,
} from '@synerg/react-components';
import { useForm } from 'react-hook-form';
import { get, uniqueId } from 'lodash';
import StepOne from '../steps/stepOne';
import StepTwo from '../steps/stepTwo';
import axios from 'axios';

const defaultValues = {
	useCaseID: null,
	clientId: '',
	clientName: '',
	companySize: 'mid',
	appStore: 'US',
	product: '',
};
export default function CreateClientProject({
	setShowCreateProjectModal,
	projectDetails,
	setToast,
}) {
	const [activeStep, setActiveStep] = useState(0);
	const [selectedUseCaseBtnIndex, setSelectedUseCaseBtnIndex] = useState(null);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const [useCases, setUseCases] = useState([]);
	const { getValues, setValue } = useForm(
		{ mode: 'onChange', defaultValues });

	useEffect(() => {
		setValue('clientName', projectDetails.clientName);
		setValue('clientId', projectDetails._id);
		setValue('product', projectDetails?.product?.shortName);

		async function getUseCases() {
			setLoading(true);
			try {
				const { data: useCases, status: useCasesStatus } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/si/project/use-cases`,
						{ params: { product: projectDetails?.product?.shortName } });

				useCasesStatus === 200 ? setUseCases(useCases) : setErrorMessage(
								'Something went wrong when trying to get use-cases. Please try again.');
				if (useCasesStatus !== 200) {
          setErrorMessage(
										'Something went wrong when trying to get use-cases. Please try again.');
					setError(true);
				}
				setLoading(false);
			} catch (e) {
				setErrorMessage(
										'Something went wrong when trying to get use-cases. Please try again.');
				setLoading(false);
				setError(true);
			}
		}

		getUseCases();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	const backClickHandler = () => {
		if (activeStep !== 0) {
			setActiveStep(activeStep - 1);
		}
	};

	const nextClickHandler = async () => {
		if (activeStep < 1) {
			setActiveStep(activeStep + 1);
		} else {
			await createProject();
		}
	};

	const canGoNext = () => {
		switch (activeStep) {
			case 0:
				if (selectedUseCaseBtnIndex !== null) {
					return true;
				}
				break;
			case 1:
				return true;
			default:
				return false;
		}
	};

	const ShowError = ({ message }) => {
		return (<SdfAlert key={uniqueId()} status={'error'} showToast={true}>
			<span slot="title"> Error </span>
			<div>{message || 'Something went wrong, please try again later'}</div>
		</SdfAlert>);
	};

	const createProject = async () => {
		try {
			setLoading(true);
			const {
				useCaseID,
				clientId,
			} = getValues();
			const data = {
				useCaseID, clientId,
			};
			const { status } = await axios.post(
				`${import.meta.env.VITE_ROOT_PATH}/api/si/project/create`, data);
			if (status === 200) {
				setTimeout(() => {
					showSuccess(`Project created successfully`);
				  setLoading(false);
				  setShowCreateProjectModal(false);
				}, 5000);
			} else {
				setLoading(false);
				setError(true);
				setErrorMessage(
					'Something went wrong when trying to create project. Please try again.');
			}
		} catch (e) {
			setLoading(false);
			const errorMessage = get(e, 'response.data.message',
				'Something went wrong when trying to create project. Please try again.');
			setErrorMessage(errorMessage);
			setError(true);
		}
	};

	const showSuccess = (message) => {
		setToast({
			key: uniqueId(), status: 'success', message: message, showToast: true,
		});
	};

	return (<div>
		<SdfFocusPane
			dismissLabel="Cancel"
			acceptLabel="Next"
			spacing="xl"
			visible={true}
			size="xl"
			hideFooter={true}
		>
			<div className="p-2 pt-4">
				<span slot="title" className="text-heading-06 font-black">
					Create new project
				</span>
				<SdfWizardLayout>
					{error &&
						<div slot="page-content" className="px-8 mb-2 min-w-fit">
							<ShowError message={errorMessage} />
						</div>}
					<div slot="page-content" className="px-8 hxl overflow-y-scroll">
						{activeStep === 0 &&
							<StepOne state={getValues()} useCases={useCases}
							         selectedUseCaseBtnIndex={selectedUseCaseBtnIndex}
							         setSelectedUseCaseBtnIndex={setSelectedUseCaseBtnIndex}
							         setValue={setValue} />}
						{activeStep === 1 && <StepTwo state={getValues()} />}
					</div>

					<SdfWizardStepsVertical slot="wizard-steps"
						className="flex-grow-0 w-64"
						style={{ marginTop: '30px' }}>
						<SdfWizardStepsVerticalItem className="w-64"
							isCurrent={activeStep === 0}
							stepStatus={activeStep > 0
								? 'visited'
								: 'unvisited'}>
							Use case selection
						</SdfWizardStepsVerticalItem>
						<SdfWizardStepsVerticalItem className="w-64"
							stepStatus={activeStep > 1
								? 'visited'
								: 'unvisited'}
							isCurrent={activeStep === 1}>
							Review
						</SdfWizardStepsVerticalItem>
					</SdfWizardStepsVertical>

					<SdfButton slot="buttons" emphasis="tertiary" variant="contained"
						onClick={() => {
							setShowCreateProjectModal(false);
						}}> Cancel </SdfButton>
					{activeStep > 0 && <SdfButton slot="buttons" variant="contained"
						onClick={backClickHandler}>
						<SdfIcon icon="nav-move-left" /> Back
					</SdfButton>}
					<SdfButton disabled={!canGoNext() ? 'true' : undefined}
						slot="buttons" emphasis="primary" iconPlacement="after"
						variant="contained" onClick={nextClickHandler}>
						{activeStep === 1 ? 'Generate' : 'Next'}
						{activeStep !== 1 && <SdfIcon icon="nav-move-right" />}
					</SdfButton>
				</SdfWizardLayout>
			</div>
			{loading &&
				<SdfBusyIndicator style={{ color: '#1a8099' }} size="xl"
					fullScreen={true} overlay={true} />}
		</SdfFocusPane>
	</div>);
}
